import { useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "./use-debounced-callback";

// useAutosave — debounced auto-save with explicit SaveStatus tracking.
//
// Phase 7 sub-bucket 11. The admin form builder runs three independent
// auto-save streams (definition / workflow / automation), each with its own
// SaveStatus indicator. Aggregating the SaveStatus values into a single
// header pill happens in the page component (`error > saving > saved > idle`
// precedence).
//
// Usage:
//   const save = useAutosave({
//     onFlush: (next) => mutation.mutateAsync({ id, definition: next }),
//     wait: 500,
//   });
//   save.queue(nextDefinition);
//   save.status; // 'idle' | 'saving' | 'saved' | 'error'
//
// The `onFlush` callback returns a Promise so the hook can transition
// `saving` → `saved` / `error` based on resolution. `saved` auto-decays back
// to `idle` after 1500ms (matches legacy FormBuilderShell's SaveIndicator
// behaviour).
//
// Critical correctness point for the SPA's per-field optimistic writes:
// `onFlush` receives the value passed to `queue()`. If the value being
// auto-saved is read out of the TanStack cache (e.g. the canvas's whole
// definition), the caller MUST re-read `queryClient.getQueryData()` inside
// `onFlush` rather than closing over a stale snapshot — otherwise a
// per-field write that landed during the typing debounce window gets
// clobbered by the section-rename flush. The hook intentionally doesn't
// know about TanStack; that's the caller's responsibility.

export type SaveStatus = "idle" | "saving" | "saved" | "error";

const SAVED_DECAY_MS = 1500;

export function useAutosave<TValue>({
  onFlush,
  wait,
}: {
  onFlush: (next: TValue) => Promise<unknown>;
  wait: number;
}): {
  status: SaveStatus;
  queue: (next: TValue) => void;
  flush: () => void;
  reset: () => void;
} {
  const [status, setStatus] = useState<SaveStatus>("idle");
  const decayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushFn = useCallback(
    (next: TValue) => {
      if (decayTimer.current) {
        clearTimeout(decayTimer.current);
        decayTimer.current = null;
      }
      setStatus("saving");
      void onFlush(next)
        .then(() => {
          setStatus("saved");
          decayTimer.current = setTimeout(() => {
            setStatus("idle");
            decayTimer.current = null;
          }, SAVED_DECAY_MS);
        })
        .catch(() => {
          setStatus("error");
        });
    },
    [onFlush],
  );

  const debounced = useDebouncedCallback(flushFn, wait);

  const queue = useCallback(
    (next: TValue) => {
      setStatus("saving");
      debounced(next);
    },
    [debounced],
  );

  const flush = useCallback(() => {
    debounced.flush();
  }, [debounced]);

  const reset = useCallback(() => {
    debounced.cancel();
    if (decayTimer.current) {
      clearTimeout(decayTimer.current);
      decayTimer.current = null;
    }
    setStatus("idle");
  }, [debounced]);

  useEffect(() => {
    return () => {
      if (decayTimer.current) clearTimeout(decayTimer.current);
    };
  }, []);

  return { status, queue, flush, reset };
}

// Aggregate multiple SaveStatus values into one with precedence:
// error > saving > saved > idle.
export function aggregateSaveStatus(...statuses: SaveStatus[]): SaveStatus {
  if (statuses.includes("error")) return "error";
  if (statuses.includes("saving")) return "saving";
  if (statuses.includes("saved")) return "saved";
  return "idle";
}
